import { AuthData } from 'lib-shared-auth';
import { AsyncStatus } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { loginApi } from 'apis/login';
import { AppLogInType } from 'app/types/app.types';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { authResolverErrorFallThroughValue } from '../../../../useAuthDataResolver.constants';

/** Represents the trigger for getting the auth data. */
export type UseAuthMagicLinkResult = {
  /** The type of the login. */
  readonly type: Extract<AppLogInType, 'magicLink'>;
  /** The async status. */
  readonly asyncStatus: Extract<AsyncStatus, 'fulfilled'>;
  /** The auth data. */
  readonly authData: AuthData;
};

/** Represents the trigger for getting the auth data. */
type Trigger = () => Promise<UseAuthMagicLinkResult>;

/**
 * A hook that resolves the auth data from the dixa login page params. When the
 * user on that page page navigates to dixa, the token and organization id gets
 * extracted from the params and passed to our login api endpoint that exchanges
 * these to auth data.
 */
export const useAuthMagicLink = (): Trigger => {
  const dispatch = useAppDispatch();
  const {
    params: { loginToken, orgId: organizationId },
    unsetParams,
  } = useAppSearchParams('/');

  /**
   * Gets the auth data through the login api endpoint for exchanging the login
   * token and organization id to the auth data.
   */
  const getAuthData = useCallback<Trigger>(async () => {
    if (!loginToken || !organizationId) {
      return Promise.reject(authResolverErrorFallThroughValue);
    }

    unsetParams(['refreshToken', 'orgId', 'loginToken']);

    const { data: authData } = await dispatch(
      loginApi.endpoints.createAppLogin.initiate({
        loginToken,
        organizationId,
      }),
    ).unwrap();

    return { type: 'magicLink', asyncStatus: 'fulfilled', authData };
  }, [dispatch, loginToken, organizationId, unsetParams]);

  return getAuthData;
};
